/**
 * StatusChip — the semantic status pill from the redesign mockups.
 *
 * Mockup `04-admin-portal` renders status as a tinted pill: a soft wash of the
 * semantic color behind a darker ink label, with a small solid dot leading.
 * That reads far better in a dense grid than MUI's default solid-fill Chip,
 * and it keeps the row's visual weight on the data rather than the badge.
 *
 * Color comes from the theme palette via `alpha()`, so both the wash and the
 * ink track light/dark automatically — no per-mode hex (SPEC.md).
 *
 * Callers usually pass a raw wire status and let `statusTone()` map it:
 *
 *   <StatusChip status={row.status} label={t(`STATUS.${row.status}`)} />
 *
 * Pass `tone` directly for statuses outside the tow-request enum.
 */
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import type { ChipProps } from '@mui/material/Chip';
import { alpha, useTheme } from '@mui/material/styles';
import { statusTone, type StatusTone } from '@/core/utils/statusTone';

export interface StatusChipProps extends Omit<ChipProps, 'color' | 'label'> {
  /** Raw wire status. Used to derive the tone when `tone` is not given. */
  status?: string | null;
  /** Explicit tone — wins over the one derived from `status`. */
  tone?: StatusTone;
  /** Display label. Callers should pass an already-translated string. */
  label: React.ReactNode;
  /** Hide the leading dot (e.g. when the chip already carries an icon). */
  hideDot?: boolean;
}

export default function StatusChip({
  status,
  tone,
  label,
  hideDot = false,
  sx,
  ...rest
}: StatusChipProps) {
  const theme = useTheme();
  const resolved = tone ?? statusTone(status);
  const isDark = theme.palette.mode === 'dark';

  // `neutral` has no palette slot — it borrows the text/divider ramp so it
  // still reads as "no state" rather than as a muted semantic color.
  const base =
    resolved === 'neutral'
      ? theme.palette.text.secondary
      : theme.palette[resolved].main;

  return (
    <Chip
      size="small"
      label={
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
          {!hideDot && (
            <Box
              component="span"
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: 'currentColor',
                flex: 'none',
              }}
            />
          )}
          {label}
        </Box>
      }
      sx={{
        // Light mode darkens the ink for AA contrast against the pale wash;
        // dark mode lightens it instead, for the same reason.
        color: isDark
          ? theme.palette.augmentColor({ color: { main: base } }).light
          : theme.palette.augmentColor({ color: { main: base } }).dark,
        backgroundColor: alpha(base, isDark ? 0.22 : 0.13),
        border: 'none',
        ...sx,
      }}
      {...rest}
    />
  );
}
